import {
	ChangeEvent,
	ComponentProps,
	FocusEvent,
	ReactNode,
	forwardRef,
	useCallback,
	useRef,
	useState,
} from "react";
import { useField } from "formik";
import { useIntl } from "react-intl";
import classNames from "classnames";
import { InputCore } from "app/components/form/InputCore/InputCore";
import { TextInput } from "app/components/form/TextInput/TextInput";
import { FieldLabel } from "app/components/form/Field/Field";
import { FieldHint } from "app/components/form/FieldHint/FieldHint";
import { makeStableClassName } from "app/components/form/utils/stableClassName";
import HorizontalDotLoader from "app/pages/.shared/HorizontalDotLoader";
import { inputSanitize } from "app/utils/sanitize";
import CrossIcon from "app/icons/navigation/CrossIcon";
import { ICON_SIZES } from "app/constants";
import styles from "./FormikTextField.module.scss";

type LabelFloatOverrideArgs = {
	meta: ReturnType<typeof useField>[1];
	field: ReturnType<typeof useField>[0];
	loading?: boolean;
};

// `pattern` uses "X" as a one-character placeholder (e.g. "XXXX XXXX XXXX XXXX"); any other
// character is a literal separator. Raw content longer than the pattern's "X" slots shows
// unformatted instead of truncating.
const countPatternSlots = (pattern: string): number =>
	pattern.split("").filter(patternChar => patternChar === "X").length;

const formatByPattern = (raw: string, pattern: string): string => {
	if (raw.length > countPatternSlots(pattern)) {
		return raw;
	}

	let formatted = "";
	let rawIndex = 0;

	for (const patternChar of pattern) {
		if (rawIndex >= raw.length) {
			break;
		}
		if (patternChar === "X") {
			formatted += raw[rawIndex];
			rawIndex += 1;
		} else {
			formatted += patternChar;
		}
	}

	return formatted;
};

// Strips every non-digit character. Used only when the opt-in `numericOnly` prop is set — kept
// separate from `pattern`'s own literal-stripping since a pattern's placeholder can represent
// non-digit content (e.g. "XXX XX" for an alphanumeric reference).
const stripNonDigits = (value: string): string => value.replace(/\D/g, "");

// Reverses formatByPattern: drops every character the pattern uses as a literal separator.
// Position-unaware, not a full mask-editing engine — fine since separators aren't meaningful content.
const stripPatternLiterals = (value: string, pattern: string): string => {
	const literalChars = new Set(pattern.split("").filter(patternChar => patternChar !== "X"));

	if (literalChars.size === 0) {
		return value;
	}

	return value
		.split("")
		.filter(char => !literalChars.has(char))
		.join("");
};

type FormikTextFieldProps = {
	id: string;
	name: string;
	label: ReactNode;
	type?: string;
	isRequired?: boolean;
	disabled?: boolean;
	readOnly?: boolean;
	loading?: boolean;
	note?: ReactNode;
	hint?: string;
	className?: string;
	maxLength?: number;
	clearable?: boolean;
	handleClearInput?: () => void;
	shouldSanitize?: boolean;
	labelFloatOverride?: (args: LabelFloatOverrideArgs) => boolean;
	onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
	"data-testid"?: string;
	// Figma leading/trailing icon slots (node 18890:1870), fixed 20px box — pass size={20} on the
	// icon to match. Consumer widens --field-input-inset/--field-label-inset when leadingIcon is set.
	leadingIcon?: ReactNode;
	trailingIcon?: ReactNode;
	// "X"-placeholder pattern for progressive display formatting (e.g. "XXXX XXXX XXXX XXXX").
	// Use type="text" (the default): type="number"/"tel" reject the literal separators it inserts.
	pattern?: string;
	// Opt-in hard block of non-digit characters on every keystroke/paste. Independent of `pattern`
	// (a pattern's "X" can represent non-digit content) — combine both for a numeric masked field.
	numericOnly?: boolean;
} & Omit<
	ComponentProps<"input">,
	"id" | "name" | "type" | "className" | "onFocus" | "ref" | "pattern" | "value" | "checked"
>;

// Layer 2 adapter: same public contract/behavior as legacy InputFormik (see InputFormik.test.jsx),
// rendered with design-system primitives instead of hand-rolled markup. forwardRef is required
// the same way InputFormik needs it for react-phone-number-input's InputComponent contract.
const FormikTextField = forwardRef<HTMLInputElement, FormikTextFieldProps>(
	(
		{
			id,
			name,
			type = "text",
			isRequired,
			disabled,
			readOnly,
			loading,
			label,
			note,
			hint,
			onFocus,
			className,
			maxLength,
			clearable = false,
			handleClearInput = () => {},
			shouldSanitize = true,
			labelFloatOverride,
			"data-testid": dataCy,
			leadingIcon,
			trailingIcon,
			pattern,
			numericOnly = false,
			...restProps
		},
		ref
	) => {
		const localRef = useRef<HTMLInputElement>(null);
		const inputRef = (ref as React.RefObject<HTMLInputElement>) ?? localRef;
		const [hasChanged, setHasChanged] = useState(false);
		// Independent of `meta.touched`: a failed submit touches every field, which used to wrongly float an empty+invalid label (FQ-1143).
		const [isFocused, setIsFocused] = useState(false);
		const [field, meta, helpers] = useField(name);
		const { formatMessage } = useIntl();

		// "Latest ref" pattern: setTouchedOnFocus/setUntouchedOnBlur below intentionally stay
		// referentially stable (empty dep array) so TextInput doesn't get a new onFocus/onBlur on
		// every keystroke. Without this, they'd permanently pin whichever onFocus/onBlur instance
		// existed on mount — silently ignoring a consumer that passes a new inline function each
		// render (a common pattern). Updated every render, read only from inside the handlers below.
		const onFocusRef = useRef(onFocus);
		onFocusRef.current = onFocus;
		const onBlurRef = useRef(restProps.onBlur);
		onBlurRef.current = restProps.onBlur;
		// Same "latest ref" reasoning applies to handleChange/handlePatternChange below: their own
		// dep arrays don't (and shouldn't) include restProps.onChange, so reading it directly would
		// silently pin whichever onChange instance existed when the callback was last recreated.
		const onChangeRef = useRef(restProps.onChange);
		onChangeRef.current = restProps.onChange;

		// Exact same rule as InputFormik. Hiding the label when disabled (Figma 2.5) was reverted:
		// it broke AddressAutocompleteFormik, whose "disabled" is a transient loading flag, not permanent.
		// Plain computation (no useMemo): it's a cheap boolean and labelFloatOverride can read any
		// field on `meta`/`field`, so a memo dep array can never safely list "everything it might use".
		const isLabelFloated = (() => {
			if (loading) {
				return true;
			}
			if (typeof labelFloatOverride === "function") {
				return labelFloatOverride({ meta, field, loading });
			}
			return isFocused || Boolean(field.value);
		})();

		const setTouchedOnFocus = useCallback((event: FocusEvent<HTMLInputElement>) => {
			if (typeof onFocusRef.current === "function") {
				onFocusRef.current(event);
			}
			setIsFocused(true);
			helpers.setTouched(true);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		const setUntouchedOnBlur = useCallback((event: FocusEvent<HTMLInputElement>) => {
			setIsFocused(false);
			if (event.target.value === "") {
				helpers.setTouched(false);
			}
			setHasChanged(false);

			if (typeof onBlurRef.current === "function") {
				onBlurRef.current(event as unknown as FocusEvent<HTMLInputElement>);
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		const handleChange = useCallback(
			(event: ChangeEvent<HTMLInputElement>) => {
				const rawValue = event.target.value;
				const value = numericOnly ? stripNonDigits(rawValue) : rawValue;

				const handleValueChange = (nextValue: string) => {
					// `typeof maxLength !== "number"` already covers "no cap set"; the remaining
					// check must not treat maxLength === 0 as "no cap" too (0 is a valid, if
					// unusual, cap meaning "no input allowed").
					if (typeof maxLength !== "number" || nextValue.length <= maxLength) {
						helpers.setValue(inputSanitize(nextValue));
					}
				};

				handleValueChange(value);

				if (!hasChanged && value !== "") {
					helpers.setError(undefined);
					setHasChanged(true);
				}

				if (typeof onChangeRef.current === "function") {
					onChangeRef.current(event);
				}
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[maxLength, hasChanged, numericOnly]
		);

		// pattern mode bypasses maxLength/inputSanitize entirely — no length cap (overflow is
		// explicitly allowed, see formatByPattern's "pattern breaks" case above), and
		// stripPatternLiterals already only ever removes pattern-owned separator characters.
		const handlePatternChange = useCallback(
			(event: ChangeEvent<HTMLInputElement>) => {
				const strippedValue = pattern
					? stripPatternLiterals(event.target.value, pattern)
					: event.target.value;
				const rawValue = numericOnly ? stripNonDigits(strippedValue) : strippedValue;

				helpers.setValue(rawValue);

				if (!hasChanged && rawValue !== "") {
					helpers.setError(undefined);
					setHasChanged(true);
				}

				if (typeof onChangeRef.current === "function") {
					onChangeRef.current(event);
				}
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[pattern, hasChanged, numericOnly]
		);

		const clearInput = () => {
			helpers.setValue("");
			helpers.setTouched(true);
			handleClearInput();
		};

		const stableClassName = makeStableClassName(dataCy);

		const hintId = hint ? `${id}-hint` : undefined;
		// Matches InputFormik's exact current predicate — see InputFormik.test.jsx: it reduces
		// to just meta.error (shows before touch). Preserved as-is; not an approved timing
		// change (migration plan section 2.2 / decision log, "error visibility inconsistency").
		const invalid = Boolean(meta.error);

		const clearButton =
			clearable && field.value ? (
				<button
					type="button"
					className={classNames(
						styles["formik-text-field__clear"],
						stableClassName("formik-text-field__clear")
					)}
					onClick={clearInput}
					aria-label={formatMessage({
						id: "general.clear.button.label",
						defaultMessage: "Clear",
					})}
				>
					<CrossIcon size={ICON_SIZES.XXS} />
				</button>
			) : undefined;

		const leadingIconSlot = leadingIcon ? (
			<span
				className={classNames(
					styles["formik-text-field__leading-icon"],
					stableClassName("formik-text-field__leading-icon")
				)}
			>
				{leadingIcon}
			</span>
		) : undefined;

		const trailingIconSlot = trailingIcon ? (
			<span
				className={classNames(
					styles["formik-text-field__trailing-icon"],
					stableClassName("formik-text-field__trailing-icon")
				)}
			>
				{trailingIcon}
			</span>
		) : undefined;

		// Clear button wins when both are active at once — it's an interactive control, not just
		// decoration, so it shouldn't silently disappear because a consumer also passed a
		// trailingIcon.
		const trailingSlotContent = clearButton ?? trailingIconSlot;

		// field.value stays raw digits (see handlePatternChange) — only the displayed value gets
		// the pattern's spaces, formatted progressively as far as the user has typed, no
		// placeholder characters for the untyped rest.
		const displayValue = pattern
			? formatByPattern(String(field.value ?? ""), pattern)
			: undefined;

		if (loading) {
			return (
				<div
					className={classNames(styles["formik-text-field"], className)}
					data-testid={dataCy}
				>
					<div className={styles["formik-text-field__loader"]}>
						<HorizontalDotLoader dotSize="3px" color="#3493dc" />
					</div>
				</div>
			);
		}

		return (
			<div
				className={classNames(styles["formik-text-field"], className)}
				data-testid={dataCy}
			>
				<InputCore
					invalid={invalid}
					readOnly={readOnly}
					disabled={disabled}
					data-floated={isLabelFloated || undefined}
					className={classNames(
						styles["formik-text-field__core"],
						stableClassName("formik-text-field__core")
					)}
					leadingSlot={leadingIconSlot}
					trailingSlot={trailingSlotContent}
				>
					<TextInput
						ref={inputRef}
						type={type}
						id={id}
						disabled={disabled}
						readOnly={readOnly}
						placeholder=" "
						// Native maxLength counts the pattern's own literal separators, so it would cap
						// the raw digit count below what `maxLength` actually promises — see
						// countPatternSlots/formatByPattern above. Not applicable in pattern mode.
						maxLength={pattern ? undefined : maxLength}
						aria-invalid={invalid || undefined}
						aria-required={isRequired || undefined}
						aria-describedby={hintId}
						{...field}
						{...restProps}
						onFocus={setTouchedOnFocus}
						onBlur={setUntouchedOnBlur}
						// Contract: shouldSanitize={false} (with no `pattern`) hands the underlying
						// <input> entirely to restProps.onChange — this component adds no onChange of
						// its own in that case. The consumer's onChange MUST call
						// helpers.setValue()/setFieldValue() itself (see PhoneIntl.jsx, whose
						// react-phone-number-input-driven onChange formats the value before syncing
						// it to Formik) — otherwise the field stays a controlled input pinned to
						// field.value and typing will visibly do nothing. Not silent: this fails
						// obviously during that consumer's own integration testing.
						{...(pattern
							? { value: displayValue, onChange: handlePatternChange }
							: shouldSanitize && { onChange: handleChange })}
						className={classNames(
							styles["formik-text-field__input"],
							stableClassName("formik-text-field__input")
						)}
					/>
					<FieldLabel
						htmlFor={id}
						error={invalid}
						required={isRequired}
						data-floated={isLabelFloated || undefined}
						className={classNames(
							styles["formik-text-field__label"],
							stableClassName("formik-text-field__label")
						)}
					>
						{label}
					</FieldLabel>
				</InputCore>
				{note && (
					<div
						className={classNames(
							styles["formik-text-field__note"],
							stableClassName("formik-text-field__note")
						)}
					>
						{note}
					</div>
				)}
				{hint && (
					<FieldHint id={hintId} variant={invalid ? "error" : "help"}>
						{hint}
					</FieldHint>
				)}
			</div>
		);
	}
);

FormikTextField.displayName = "FormikTextField";

export { FormikTextField };
